import { supabase } from '../lib/supabase';

export interface Course {
  id: string;
  title: string;
  description?: string;
  instructor_id?: string;
  price: number;
  duration_weeks?: number;
  thumbnail_url?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface CourseCurriculum {
  id: string;
  course_id: string;
  section_title: string;
  section_order: number;
  content: {
    lessons: Array<{
      title: string;
      duration: string;
      video_url: string;
      description: string;
    }>;
  };
  created_at: string;
  updated_at: string;
}

interface CourseBatch {
  id: string;
  course_id: string;
  batch_name: string;
  start_date: string;
  end_date: string;
  schedule_info: {
    days: string[];
    time: string;
    timezone: string;
  };
  max_students: number;
  current_students: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface CourseReview {
  id: string;
  course_id: string;
  user_id: string;
  enrollment_id: string;
  rating: number;
  review_text: string | null;
  is_verified: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Get all courses (for admin/instructor use)
 */
export async function getAllCourses(): Promise<{ data: any[] | null; error: Error | null }> {
  try {
    const { data, error } = await supabase
      .from('courses')
      .select(`
        id,
        title,
        description,
        price,
        duration_weeks,
        thumbnail_url,
        is_active,
        instructor_id,
        user_profiles!courses_instructor_id_fkey(full_name)
      `)
      .order('created_at', { ascending: false });

    if (error) {
      return { data: null, error: new Error(error.message) };
    }

    const courses = data?.map((course: any) => ({
      ...course,
      instructor_name: course.user_profiles?.full_name
    })) || [];

    return { data: courses, error: null };
  } catch (error: any) {
    return { data: null, error: new Error(error?.message || 'Failed to fetch courses') };
  }
}

export const courseService = {
  // Add getAllCourses method to courseService object
  async getAllCourses() {
    try {
      const { data, error } = await supabase
        .from('courses')
        .select(`
          id,
          title,
          description,
          price,
          duration_weeks,
          thumbnail_url,
          is_active,
          instructor_id,
          user_profiles!courses_instructor_id_fkey(full_name)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        return { data: null, error: new Error(error.message) };
      }

      const courses = data?.map((course: any) => ({
        ...course,
        instructor_name: course.user_profiles?.full_name
      })) || [];

      return { data: courses, error: null };
    } catch (error: any) {
      return { data: null, error: new Error(error?.message || 'Failed to fetch courses') };
    }
  },

  async getCourseById(courseId: string) {
    const { data, error } = await supabase
      .from('courses')
      .select(`
        *,
        instructor:instructor_id (
          id,
          full_name,
          avatar_url
        )
      `)
      .eq('id', courseId)
      .single();

    return { data, error };
  },

  async createCourse(courseData: Omit<Course, 'id' | 'created_at' | 'updated_at'>) {
    const { data, error } = await supabase
      .from('courses')
      .insert(courseData)
      .select()
      .single();

    return { data, error };
  },

  async updateCourse(courseId: string, updates: Partial<Course>) {
    const { data, error } = await supabase
      .from('courses')
      .update(updates)
      .eq('id', courseId)
      .select()
      .single();

    return { data, error };
  },

  async deleteCourse(courseId: string) {
    const { error } = await supabase
      .from('courses')
      .delete()
      .eq('id', courseId);

    return { error };
  },

  // Curriculum Management
  async getCourseCurriculum(courseId: string) {
    try {
      const { data, error } = await supabase
        .from('course_curriculum')
        .select('*')
        .eq('course_id', courseId)
        .order('section_order', { ascending: true });

      if (error) {
        return { data: null, error: error.message };
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to fetch curriculum' };
    }
  },

  async addCurriculumSection(section: Omit<CourseCurriculum, 'id' | 'created_at' | 'updated_at'>) {
    try {
      const { data, error } = await supabase
        .from('course_curriculum')
        .insert(section)
        .select()
        .single();

      if (error) {
        return { data: null, error: error.message };
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to add curriculum section' };
    }
  },

  async updateCurriculumSection(id: string, updates: Partial<CourseCurriculum>) {
    try {
      const { data, error } = await supabase
        .from('course_curriculum')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        return { data: null, error: error.message };
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to update curriculum section' };
    }
  },

  async deleteCurriculumSection(id: string) {
    try {
      const { error } = await supabase
        .from('course_curriculum')
        .delete()
        .eq('id', id);

      if (error) {
        return { error: error.message };
      }
      return { error: null };
    } catch (error) {
      return { error: 'Failed to delete curriculum section' };
    }
  },

  // Batch Management
  async getCourseBatches(courseId: string) {
    try {
      const { data, error } = await supabase
        .from('course_batches')
        .select('*')
        .eq('course_id', courseId)
        .eq('is_active', true)
        .order('start_date', { ascending: true });

      if (error) {
        return { data: null, error: error.message };
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to fetch batches' };
    }
  },

  async createBatch(batch: Omit<CourseBatch, 'id' | 'current_students' | 'created_at' | 'updated_at'>) {
    try {
      const { data, error } = await supabase
        .from('course_batches')
        .insert(batch)
        .select()
        .single();

      if (error) {
        return { data: null, error: error.message };
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to create batch' };
    }
  },

  async updateBatch(id: string, updates: Partial<CourseBatch>) {
    try {
      const { data, error } = await supabase
        .from('course_batches')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        return { data: null, error: error.message };
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to update batch' };
    }
  },

  async deleteBatch(id: string) {
    try {
      const { error } = await supabase
        .from('course_batches')
        .delete()
        .eq('id', id);

      if (error) {
        return { error: error.message };
      }
      return { error: null };
    } catch (error) {
      return { error: 'Failed to delete batch' };
    }
  },

  // Review Management
  async getCourseReviews(courseId: string) {
    try {
      const { data, error } = await supabase
        .from('course_reviews')
        .select(`
          *,
          user:user_profiles(full_name, avatar_url)
        `)
        .eq('course_id', courseId)
        .eq('is_verified', true)
        .order('created_at', { ascending: false });

      if (error) {
        return { data: null, error: error.message };
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to fetch reviews' };
    }
  },

  async submitReview(review: Omit<CourseReview, 'id' | 'is_verified' | 'created_at' | 'updated_at'>) {
    try {
      const { data, error } = await supabase
        .from('course_reviews')
        .insert(review)
        .select()
        .single();

      if (error) {
        return { data: null, error: error.message };
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to submit review' };
    }
  },

  async updateReview(id: string, updates: Pick<CourseReview, 'rating' | 'review_text'>) {
    try {
      const { data, error } = await supabase
        .from('course_reviews')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        return { data: null, error: error.message };
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to update review' };
    }
  },

  async getUserReviewForCourse(userId: string, courseId: string) {
    try {
      const { data, error } = await supabase
        .from('course_reviews')
        .select('*')
        .eq('user_id', userId)
        .eq('course_id', courseId)
        .single();

      if (error && error.code !== 'PGRST116') {
        return { data: null, error: error.message };
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to fetch user review' };
    }
  },
};